import { FC, useMemo, useCallback, memo } from 'react'
import styles from 'components/features/pfm/TrackerCreatorPane/TrackerCreatorPane.styl'
import * as formStyles from 'styles/molecules/forms/Form.css'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { useShallowSelector, useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import TextButton from 'components/atoms/buttons/TextButton'
import Button from 'components/features/pfm/Button/Button'
import { selectedPeergroupIdSelector, yourPeergroupIdSelector, relationsSelector } from 'selectors/peergroupSelectors'
import find from 'lodash/find'
import { getMetaCategoriesByType } from 'helpers/categoriesHelpers'
import { FORM_CREATE_SPENDING_TRACKER, FORM_CREATE_INCOME_TRACKER } from 'constants/forms'
import { formValueSelector } from 'redux-form'
import { onFormSubmit } from 'helpers/formHelpers'
import {
  memberMoneyflowBreakdownSelector,
  peergroupsMoneyflowBreakdownsSelector,
  memberMoneyflowTrendsSelector,
  moneyflowTrackerSelector,
} from 'selectors/moneyflowSelectors'
import { getTrackerCategoryName } from 'helpers/categoriesHelpers'
import * as startCase from 'lodash/startCase'
import ChartHeader from 'components/atoms/charts/ChartHeader'
import { FloatingPaneLayout } from 'components/features/pfm/FloatingPaneLayout/FloatingPaneLayout'
import { SpendingTrackerLimitForm, IncomeTrackerLimitForm, TrackerLimitFormValues } from 'components/molecules/forms/TrackerLimitForm'
import { BodyText } from 'components/atoms/Text'
import { metaSelector } from 'selectors/globalSelectors'
import { ALL_CATEGORY_ID } from 'constants/meta'
import { deleteMoneyflowTrackerActionCreator } from 'actions/moneyflowActions'
import { killFloatingPanesActionCreator } from 'actions/floatingPanesActions'
import Spinner from 'components/features/pfm/Spinner/Spinner'
import { FloatingPane, TrackerFloatingPane as TrackerFloatingPaneType, TrackerFloatingPane } from 'interfaces/floatingPanes'
import { createFloatingPaneByTypeSelector } from 'selectors/floatingPanesSelectors'
import { connectLoadable, bindGuard, createParallelGuard } from 'helpers/loadableHelpers'
import { Guards } from 'interfaces/guards'
import { peergroupsGuard } from 'guards/globalGuards'
import { memberMoneyflowTrendGuard, memberMoneyflowBreakdownGuard, peergroupsMoneyflowBreakdownGuard, moneyflowTrackersGuard } from 'guards/moneyflowGuards'
import { Timeframe } from 'interfaces/chart'
import { MoneyflowType } from 'interfaces/moneyflow'
import { isMobileSelector } from 'selectors/viewportSelectors'
import { SLUGS_TRACKER_MAP } from 'constants/peergroups'
import { TRACKER_LIMIT_TYPE } from 'constants/moneyflow'
import { FloatingPaneHeader } from 'components/features/pfm/FloatingPaneHeader/FloatingPaneHeader'

const TYPED_PANE: Pick<FloatingPane, 'type'> = {
  type: 'tracker',
}

type TrackerCreatorPaneInnerProps = TrackerFloatingPane['payload']

const TrackerCreatorPaneInnerComponent: FC<TrackerCreatorPaneInnerProps> = memo((props) => {
  const { category: categoryId, type, timeframe } = props
  const formSelector = useMemo(() => {
    const formName = type === 'spending' ? FORM_CREATE_SPENDING_TRACKER : FORM_CREATE_INCOME_TRACKER
    return formValueSelector(formName)
  }, [type])
  const formLimitValue = useStoreSelector((state) => formSelector(state, 'limit')) as number

  const selectorProps = useMemo(() => ({
    type, timeframe, categoryId,
  }), [type, timeframe, categoryId])

  const memberBreakdown = useStoreSelector((state) => memberMoneyflowBreakdownSelector(state, selectorProps))
  const memberItem = memberBreakdown.byKey[categoryId]

  const peersBreakdown = useStoreSelector((state) => peergroupsMoneyflowBreakdownsSelector(state, selectorProps))
  const yourPeergroupId = useStoreSelector(yourPeergroupIdSelector) as number

  const peerItem = useMemo(() => {
    if (!peersBreakdown.byKey[yourPeergroupId] || !peersBreakdown.byKey[yourPeergroupId].breakdown.byKey[categoryId]) {
      return null
    }
    return peersBreakdown.byKey[yourPeergroupId].breakdown.byKey[categoryId]
  }, [peersBreakdown, yourPeergroupId, categoryId])

  const selectedPeergroupId = useStoreSelector(selectedPeergroupIdSelector) as number
  const otherItem = useMemo(() => {
    if (!peersBreakdown.byKey[selectedPeergroupId] || !peersBreakdown.byKey[selectedPeergroupId].breakdown.byKey[categoryId]) {
      return null
    }
    return peersBreakdown.byKey[selectedPeergroupId].breakdown.byKey[categoryId]
  }, [peersBreakdown, selectedPeergroupId, categoryId])

  const meta = useStoreSelector(metaSelector)
  const category = getMetaCategoriesByType(type, meta).byKey[categoryId]

  const incomeTrend = useStoreSelector((state) => memberMoneyflowTrendsSelector(state, {
    type: 'income',
    timeframe,
    categoryId: ALL_CATEGORY_ID,
  }))

  const selectedRelation = useShallowSelector((state) => {
    const relations = relationsSelector(state).byKey
    return find(relations, { peergroupId: selectedPeergroupId })
  })

  const tracker = useShallowSelector((state) => moneyflowTrackerSelector(state, selectorProps))

  const dispatch = useStoreDispatch()

  const handleDelete = useCallback(() => {
    if (!tracker) {
      return
    }

    dispatch(deleteMoneyflowTrackerActionCreator.request({ type, tracker }))
  }, [type, tracker, dispatch])

  const handleClose = useCallback<() => void>(() => {
    dispatch(killFloatingPanesActionCreator('tracker'))
  }, [dispatch])

  const isMobile = useStoreSelector(isMobileSelector)

  const headerChild = useMemo(() => (
    <FloatingPaneHeader
      superTitle='YOUR SPENDING TRACKER'
      title={ getTrackerCategoryName(category, type) }
      compact
    />
  ), [category, type])

  if (!incomeTrend || !peerItem || !otherItem) {
    return (
      <div className={ styles.preloader }>
        <Spinner />
      </div>
    )
  }

  const otherPeerGroupName = selectedRelation.slug ? SLUGS_TRACKER_MAP[selectedRelation.slug] : `"${ selectedRelation.name }" Avg.`

  const limit = tracker ? tracker.limit : null

  const limitText = TRACKER_LIMIT_TYPE[type]


  const trackerLimitInitialValues: TrackerLimitFormValues = {
    category: category.id,
    limit,
    isNew: limit !== null,
  }

  const trackerForm = type === 'spending' ? (
    <SpendingTrackerLimitForm
      onSubmit={ onFormSubmit }
      initialValues={ trackerLimitInitialValues }
      isMobile={ isMobile }
      avgIncome={ incomeTrend.average }
      formLimitValue={ formLimitValue }
      limitLabel={ startCase(limitText) }
    />
  ) : (
    <IncomeTrackerLimitForm
      onSubmit={ onFormSubmit }
      initialValues={ trackerLimitInitialValues }
      isMobile={ isMobile }
      avgIncome={ incomeTrend.average }
      formLimitValue={ formLimitValue }
      limitLabel={ startCase(limitText) }
    />
  )

  const trackerRender = tracker ? (
    <div className={ styles.deleteTrackerButton }>
      <Button
        fullWidth
        style='outlined'
        text={ `Remove ${ limitText }` }
        onClick={ handleDelete }
      />
    </div>
  ) : null

  return (
    <FloatingPaneLayout
      type='tracker'
      header={ headerChild }
      onClose={ handleClose }
      cardLayout
    >
      <BodyText>
        This { limitText } will be set on a monthly basis starting with the current month and reflected in the previous months.
        <br />
        <br />
        These averages may be helpful in setting your { limitText }.
      </BodyText>

      <div className={ styles.stats }>
        <ChartHeader
          member={{
            label: getMemberAmountLabel(type, timeframe),
            value: formatUSCurrency(memberItem.average),
          }}
          peers={{
            label: 'Peer Avg.',
            value: formatUSCurrency(peerItem.average),
          }}
          others={{
            label: otherPeerGroupName,
            value: formatUSCurrency(otherItem.average),
          }}
          withComparison
        />
      </div>

      <div className={ styles.formWrapper }>
        { trackerForm }
        { trackerRender }
      </div>
      <div className={ formStyles.cancelLinkWrapper }>
        <TextButton size='regular' onClick={ handleClose }>
          Cancel
        </TextButton>
      </div>
    </FloatingPaneLayout>
  )
})


const guards: Guards = [
  peergroupsGuard,
  createParallelGuard(
    memberMoneyflowBreakdownGuard, peergroupsMoneyflowBreakdownGuard,
    moneyflowTrackersGuard, memberMoneyflowTrendGuard,
    bindGuard(memberMoneyflowTrendGuard, { type: 'income', categoryId: ALL_CATEGORY_ID })
  ),
]

const spinnerChild = (
  <div className={ styles.preloader }>
    <Spinner />
  </div>
)

const TrackerCreatorPaneInner = connectLoadable(guards, () => spinnerChild)()(TrackerCreatorPaneInnerComponent)

export const TrackerCreatorPane: FC = () => {
  const paneSelector = useMemo(
    createFloatingPaneByTypeSelector,
    []
  )
  const pane = useStoreSelector((state) => paneSelector(state, TYPED_PANE)) as TrackerFloatingPaneType

  if (!pane) {
    return null
  }

  return (
    <TrackerCreatorPaneInner { ...pane.payload } />
  )
}

const getMemberAmountLabel = (type: MoneyflowType, timeframe: Timeframe): string => {
  switch (timeframe) {
    case 'current-month':
      return 'Your Projected'

    case 'last-month':
      return type === 'spending'
        ? 'Your Spending'
        : 'Your Income'

    case '6-months':
    case '13-months':
      return 'Your Monthly Avg.'
    default:
      break
  }
  return timeframe
}
